import type { SessionFilter } from "../../types/types";

import { createContext, useContext, useMemo, useState } from "react";

import { getDateRange } from "../../utils/dateRange";

export type Period =
  | "Alltime"
  | "Custom"
  | "Last Month"
  | "Last Week"
  | "Last Year"
  | "This Month"
  | "This Week"
  | "This Year"
  | "Today";

interface FilterState {
  customEnd: Date | null;
  customStart: Date | null;
  /** pre-built filter that can be sent directly to the backend */
  dateFilter: SessionFilter;
  period: Period;
  setCustomEnd: (d: Date | null) => void;
  setCustomStart: (d: Date | null) => void;
  setPeriod: (p: Period) => void;
}

export const FilterContext = createContext<FilterState | null>(null);

export const useFilter = (): FilterState => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilter must be used within a FilterContextProvider");
  }
  return context;
};

export const useFilterState = (): FilterState => {
  const [period, setPeriod] = useState<Period>("This Year");
  const [customStart, setCustomStart] = useState<Date | null>(null);
  const [customEnd, setCustomEnd] = useState<Date | null>(null);

  const dateFilter = useMemo<SessionFilter>(
    () => getDateRange(period, customStart, customEnd),
    [period, customStart, customEnd],
  );

  return {
    customEnd,
    customStart,
    dateFilter,
    period,
    setCustomEnd,
    setCustomStart,
    setPeriod,
  };
};
